import { ContentType } from '@/services/directr'
import { notEmpty } from '@/utils'
import { RequestHandler } from '@Protium/vue-ozone-utils'
import { diff } from 'deep-diff'
import { get } from 'lodash'
import { computed, reactive } from 'vue'

interface SaveState {
  saving: boolean,
  progress: number,
  errors: Array<any>,
  message: string,
  ids?: Array<any>
}

export interface DiffState<T> {
  lhs: Array<T>,
  rhs: Array<T>
}

export enum SaveHook {
  'Pre' = 'pre',
  'Post' = 'post',
  'BeforeCreate' = 'beforeCreate',
  'BeforeUpdate' = 'beforeUpdate',
  'BeforeDelete' = 'beforeDelete',
  'AfterCreate' = 'afterCreate',
  'AfterUpdate' = 'afterUpdate',
  'AfterDelete' = 'afterDelete'
}

export interface SaveHooks<T = Record<string, any>> {
  pre?(state: SaveState, diff: DiffState<T>): Promise<boolean | void>,
  beforeCreate?(item:T, state: SaveState, diff: DiffState<T>): Promise<boolean | void>,
  beforeUpdate?(item:T, state: SaveState, diff: DiffState<T>): Promise<boolean | void>,
  beforeDelete?(item:T, state: SaveState, diff: DiffState<T>): Promise<boolean | void>,
  afterCreate?(item:T, state: SaveState, diff: DiffState<T>): Promise<boolean | void>,
  afterUpdate?(item:T, state: SaveState, diff: DiffState<T>): Promise<boolean | void>,
  afterDelete?(item:T, state: SaveState, diff: DiffState<T>): Promise<boolean | void>,
  post?(state:SaveState, diff:DiffState<T>): Promise<boolean | void>
}

export default <T extends Record<string, any>>(contentType: ContentType, requestHandler: RequestHandler<any>, hookObject: SaveHooks<T> = {}, startingState?: Array<T>) => {
  const hooks: SaveHooks<T> = hookObject

  const saveState = reactive<SaveState>({
    saving: true,
    progress: 0,
    errors: [],
    message: ''
  })

  // NOTE stores starting state and edited state, i.e left-hand-side <> right-hand-side
  const diffState = reactive<DiffState<T>>({ lhs: (startingState || []), rhs: (startingState || []) })
  const currentDiff = computed(() => {
    // console.log('calculating diff...')
    return diff(diffState.lhs, diffState.rhs)
  })

  // Handler for all changes to the catalogue product list.
  async function save () {
    saveState.saving = true
    saveState.progress = 0
    saveState.errors = []
    saveState.message = 'Saving...'

    const total = (currentDiff.value || []).length
    let done = 0
    const incrementProgress = n => {
      saveState.progress = (total / 100) * done++
    }

    try {
      // console.log('save | difference', currentDiff.value)
      // NOTE these function only look for changes at the top-level,
      // since nested N & D diff-types are just considered updates within the entry in this context

      if (hooks.pre) await hooks.pre(saveState, diffState as DiffState<T>)

      // New items
      const creates = (currentDiff.value || []).map(item => {
        // NOTE since we expect arrays on both sides of the diff, the first diff layer will always be f type "A"
        // and the deep-diff types force us to explicitly check for it before we can access the nested properties safely
        if (item.kind !== 'A') return null

        return (item.item.kind === 'N' && item.item.rhs)
          ? item.item.rhs // new item will naturally only have a rhs value, we take
          : null
      }).filter(f => f !== null && f !== undefined).filter((f, i, a) => a.indexOf(f) === i)

      saveState.message = `Adding ${creates.length} new products...`
      console.log(saveState.message)
      const created = await Promise.allSettled(creates.map(async (rhs: any, n) => {
        // console.log('create -> ', rhs)
        if (rhs) rhs.sys.draft = false
        // fix for when you create items and save, and then click on delete after, which will visually delete without confirm

        if (!get(rhs, 'sys.id')) {
          console.warn(`DiffSaveHandler | could not create at index ${n}. right-hand-side item has no sys.id`, rhs)
          return Promise.resolve()
        }

        const item:T = rhs as unknown as T // fukit cast

        if (hooks.beforeCreate) await hooks.beforeCreate(item, saveState, diffState as DiffState<T>)

        // put can also handle as "create" and since we technically do not know the request handler, create can be undefined
        return requestHandler.put(contentType, [], item)
          .then(async res => {
            incrementProgress(n)
            if (hooks.afterCreate) await hooks.afterCreate(item, saveState, diffState as DiffState<T>)
          }).catch(error => {
            saveState.errors.push({ item, error })
            incrementProgress(n)
          })
      }))

      // Updates
      const updates = (currentDiff.value || []).map(item => {
        return !(item.kind === 'D' && (item.path || []).length === 1) &&
          !(item.kind !== 'N' && (item.path || []).length === 1) &&
          !(item.kind === 'N' && item.path?.slice(1).every(n => ['sys', 'delete'].includes(n)))
          ? (item.path || [])[0] // path[0] will be the array index
          : null
      }).filter(f => f !== null && f !== undefined).filter((f, i, a) => a.indexOf(f) === i)

      saveState.message = `Updating ${updates.length} products...`
      console.log(saveState.message)
      const updated = await Promise.allSettled(updates.map(async (index, n) => {
        // console.log('updating', index, rhs[index])
        if (!get(diffState.rhs[index], 'sys.id')) {
          console.warn(`DiffSaveHandler | could not update at index ${index}. right-hand-side item has no sys.id`, diffState.rhs[index])
          return Promise.resolve()
        }

        if (hooks.beforeUpdate) await hooks.beforeUpdate(diffState.rhs[index] as any, saveState, diffState as DiffState<T>)

        return requestHandler.put(contentType, get(diffState.rhs[index], 'sys.id'), diffState.rhs[index])
          .then(async res => {
            incrementProgress(n)
            if (hooks.afterUpdate) await hooks.afterUpdate(diffState.rhs[index] as any, saveState, diffState as DiffState<T>)
          }).catch(error => {
            saveState.errors.push({ item: diffState.rhs[index], error })
            incrementProgress(n)
          })
      }))

      // Deletes
      // NOTE a "delete sys flag is set for deleting entries, since splicing the array will confuse the diff into thinking every other array item has changed, and the last array item has been removed..."
      const deletes:Array<{item: any, index: number}> = (currentDiff.value || [])
        .map(item => {
          // if (item.kind !== 'A') return null
          return item.kind === 'N' && item.path?.slice(1).every(n => ['sys', 'delete'].includes(n))
            ? { item: diffState.rhs[item.path[0]], index: item.path[0] }
            : null
        })
        .filter(notEmpty)
        .filter((f, i, a) => a.indexOf(f) === i)

      saveState.message = `Deleting ${deletes.length} products...`
      // console.log(saveState.message, currentDiff.value, '|', diffState)
      const deleted = await Promise.allSettled(deletes.map(async ({ item, index }, n) => {
        // console.log('deleting', index, diffState.rhs[index])
        if (!get(item, 'sys.id')) {
          console.warn('DiffSaveHandler | could not delete item. left-hand-side item has no sys.id', item)
          return Promise.resolve()
        }

        if (hooks.beforeDelete) await hooks.beforeDelete(item as unknown as T, saveState, diffState as DiffState<T>)

        return requestHandler.delete(contentType, get(item, 'sys.id'))
          .then(async res => {
            incrementProgress(n)
            diffState.rhs.splice(index, 1)
            if (hooks.afterDelete) await hooks.afterDelete(item as unknown as T, saveState, diffState as DiffState<T>)
          }).catch(error => {
            saveState.errors.push({ item: item, error })
            incrementProgress(n)
          })
      }))
    } catch (e) {
      console.error(e)
      saveState.errors.push(e)
    } finally {
      if (hooks.post) await hooks.post(saveState, diffState as DiffState<T>)

      saveState.saving = false
      saveState.message = 'Products processed'
      saveState.progress = 100
      setTimeout(() => { saveState.message = '' }, 3000)
      showSaveResult()
    }
  }

  function showSaveResult () {
    console.log(`Saving completed with ${saveState.errors.length} errors`)
    console.log(saveState.errors)
  }

  function registerHook (hook: SaveHook, fn) {
    hooks[hook] = fn
  }

  return {
    save,
    saveState,
    diffState,
    currentDiff,
    registerHook,
    showSaveResult
  }
}
